import React, { useState } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Paper,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  Badge,
  Avatar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Build as BuildIcon,
  Add as AddIcon,
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  PlayCircleFilled as InProgressIcon,
  Cancel as CancelledIcon,
  Flag as FlagIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Euro as EuroIcon,
  MoneyOff as MoneyOffIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

// Create a motion component for the Card
const MotionCard = motion(Card);

// Create a motion component for the Grid container
const MotionGrid = motion(Grid);

// Create a motion component for the Grid item
const MotionGridItem = motion(Grid);

const CarTasksSection = ({ 
  car, 
  tasks, 
  showCompletedTasks, 
  setShowCompletedTasks, 
  setCreateTaskModalOpen,
  isCarOwner,
  getStatusIcon,
  getPriorityColor,
  getPaymentStatusIcon,
  tasksExpanded,
  setTasksExpanded
}) => {
  const { t } = useTranslation('cars');
  const navigate = useNavigate();
  
  // Add state to track image errors
  const [imageErrors, setImageErrors] = useState({});
  
  // Handle image error
  const handleImageError = (userId) => {
    setImageErrors(prev => ({
      ...prev,
      [userId]: true
    }));
  };
  
  // Filter tasks based on the toggle state
  const filteredTasks = tasks.filter(task => {
    if (!showCompletedTasks) {
      return task.status !== 'completed';
    }
    return true;
  });

  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  return (
    <Paper elevation={3} sx={{ mb: 3 }}>
      <Box p={2}>
        <Box 
          display="flex" 
          alignItems="center" 
          mb={tasksExpanded ? 2 : 0}
          onClick={() => setTasksExpanded(!tasksExpanded)}
          sx={{ cursor: 'pointer' }}
        >
          <Box display="flex" alignItems="center">
            <BuildIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">{t('carDetail.tasks')}</Typography>
          </Box>
          
          <Box ml="auto" display="flex" alignItems="center">
            {/* Add expand/collapse icon */}
            {tasksExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        </Box>
        
        <Collapse in={tasksExpanded}>
            
            <Box display="flex" width="100%" mb={2}>
                {tasks.length > 0 && tasksExpanded && (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={showCompletedTasks ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    onClick={(e) => {
                    e.stopPropagation(); // Prevent accordion toggle
                    setShowCompletedTasks(!showCompletedTasks);
                    }}
                    sx={{ width: '50%', mr: 1 }}
                >
                    {showCompletedTasks ? t('carDetail.hideCompleted') : t('carDetail.showCompleted')}
                </Button>
                )}
                
                {isCarOwner() && tasksExpanded && (
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={(e) => {
                    e.stopPropagation(); // Prevent accordion toggle
                    setCreateTaskModalOpen(true);
                    }}
                    sx={{ width: '50%' }}
                >
                    {t('carDetail.addTask')}
                </Button>
                )}
                
            </Box>

          {filteredTasks.length > 0 ? (
            <MotionGrid 
              container 
              spacing={2} 
              variants={containerVariants}
              initial="hidden"
              animate="show"
            >
              <AnimatePresence>
                {filteredTasks.map((task, index) => (
                  <MotionGridItem 
                    item 
                    xs={12} 
                    sm={12} 
                    md={12} 
                    key={task._id}
                    variants={itemVariants}
                    custom={index}
                    exit={{ 
                      opacity: 0, 
                      y: -20, 
                      transition: { duration: 0.2 } 
                    }}
                    layout
                  >
                    <MotionCard 
                      variant="outlined" 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        borderLeft: `4px solid ${getPriorityColor(task.priority)}`,
                        background: task.status === 'completed' 
                          ? 'rgba(76, 175, 80, 0.08)' 
                          : `linear-gradient(to right, ${getPriorityColor(task.priority)}10, transparent)`,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3
                        }
                      }}
                      onClick={() => navigate(`/workshop/tasks/${task._id}`)}
                      whileHover={{ 
                        scale: 1.02,
                        boxShadow: "0px 5px 15px rgba(0,0,0,0.1)"
                      }}
                    >
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                          <Box display="flex" alignItems="center">
                            {getStatusIcon(task.status, task)}
                            <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'bold' }}>
                              {task.title}
                            </Typography>
                          </Box>
                          <Chip 
                            label={t(`workshop:priority.${task.priority}`)} 
                            size="small" 
                            sx={{ 
                              bgcolor: getPriorityColor(task.priority),
                              color: 'white',
                              fontWeight: 'bold',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              '&:hover': {
                                opacity: 0.9
                              }
                            }} 
                          />
                        </Box>
                        
                        {task.description && (
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {task.description}
                          </Typography>
                        )}
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center">
                            <Tooltip title={t('carDetail.assignedTo')}>
                              <Box display="flex" alignItems="center">
                                <Avatar 
                                  src={!imageErrors[task.assignedTo?._id] && task.assignedTo?.imageUrl ? task.assignedTo.imageUrl : ''}
                                  onError={() => handleImageError(task.assignedTo?._id)}
                                  sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.main' }}
                                >
                                  {task.assignedTo?.name?.charAt(0) || task.assignedTo?.username?.charAt(0) || '?'}
                                </Avatar>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  {task.assignedTo?.name || task.assignedTo?.username || t('common:unknown')}
                                </Typography>
                              </Box>
                            </Tooltip>
                            
                            {task.status === 'completed' && task.completedDate && (
                              <Tooltip title={t('carDetail.completedAt')}>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                  {format(new Date(task.completedDate), 'dd/MM/yyyy')}
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                          
                          <Box display="flex" alignItems="center">
                            {task.financialTracking && getPaymentStatusIcon(task)}
                            
                            {task.actualHours > 0 && (
                              <Tooltip title={t('carDetail.hoursSpent')}>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  {task.actualHours}h
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </MotionCard>
                  </MotionGridItem>
                ))}
              </AnimatePresence>
            </MotionGrid>
          ) : (
            <Box py={4} textAlign="center">
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {filteredTasks.length === 0 && tasks.length > 0 
                  ? t('carDetail.noVisibleTasks') 
                  : t('carDetail.noTasks')}
              </Typography>
              {isCarOwner() && tasks.length === 0 && (
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={() => setCreateTaskModalOpen(true)}
                  sx={{ mt: 2 }}
                >
                  {t('carDetail.createFirstTask')}
                </Button>
              )}
            </Box>
          )}
        </Collapse>
      </Box>
    </Paper>
  );
};

export default CarTasksSection; 